import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "../../lib/utils";

export interface MultiselectOption {
  value: string;
  label: string;
}

interface MultiselectProps {
  options: MultiselectOption[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  color?: "green" | "yellow" | "blue" | "red" | "purple" | "gray";
  multipleSelectedColor?:
    | "green"
    | "yellow"
    | "blue"
    | "red"
    | "purple"
    | "gray";
  disabled?: boolean;
  width?: string;
  multipleSelectionLabel?: string; // e.g., "Samples", "Categories", "Items"
}

const colorVariants = {
  green: "bg-green-100 text-green-800 border-green-200",
  yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  red: "bg-red-100 text-red-800 border-red-200",
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  gray: "bg-gray-100 text-gray-800 border-gray-200",
};

export const Multiselect: React.FC<MultiselectProps> = ({
  options,
  value = [],
  onValueChange,
  placeholder = "Select",
  className,
  color = "green",
  multipleSelectedColor = "yellow",
  disabled = false,
  width = "w-[180px]",
  multipleSelectionLabel = "Items",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Determine if dropdown should open upward and calculate position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 240; // max-h-60 = 240px

      // Check if there's enough space below
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const shouldOpenUpward =
        spaceBelow < dropdownHeight && buttonRect.top > dropdownHeight;

      setOpenUpward(shouldOpenUpward);
      setDropdownPosition({
        top: shouldOpenUpward
          ? buttonRect.top - 4 // Position above button
          : buttonRect.bottom + 4, // Position below button
        left: buttonRect.left,
        width: buttonRect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onValueChange?.(newValue);
  };

  const handleClear = () => {
    onValueChange?.([]);
  };

  const displayLabel =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? options.find((o) => o.value === value[0])?.label || placeholder
        : `${value.length} ${multipleSelectionLabel}`;

  const hasSelection = value.length > 0;
  const hasMultipleSelection = value.length > 1;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        className={cn(
          "inline-flex h-9 items-center justify-between rounded-md border border-stone-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-stone-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          width,
          hasSelection &&
            (hasMultipleSelection
              ? colorVariants[multipleSelectedColor]
              : colorVariants[color]),
          className,
        )}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
          }
        }}
        disabled={disabled}
        type="button"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 opacity-50 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] rounded-md border border-stone-200 bg-white shadow-lg"
            style={{
              top: openUpward ? "auto" : dropdownPosition.top,
              bottom: openUpward
                ? window.innerHeight - dropdownPosition.top
                : "auto",
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            <div className="max-h-60 overflow-auto py-1">
              {options.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 px-2 py-1.5 cursor-pointer hover:bg-stone-100 text-sm"
                    onClick={() => handleToggleOption(option.value)}
                  >
                    <div
                      className={cn(
                        "h-4 w-4 shrink-0 rounded-[4px] border border-stone-300 flex items-center justify-center",
                        isSelected &&
                          "bg-stone-950 border-stone-950 text-white",
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <span className="truncate">{option.label}</span>
                  </div>
                );
              })}
              {value.length > 0 && (
                <>
                  <div className="border-t my-1" />
                  <div
                    className="flex items-center space-x-2 px-2 py-1.5 cursor-pointer hover:bg-stone-100 text-red-600 text-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClear();
                    }}
                  >
                    <span>Clear</span>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
